import {
  partDisplayType,
  type ThreadMessageLike,
} from '@/components/agent-chat/question-card.util';
import { asRecord } from '@/components/agent-chat/as-record.util';

/**
 * A one-off email or phone check (`validation_results`): its rows as the card
 * shows them and as Copy writes them, one line a value in the order pasted.
 * Copy is the deterministic way to get every row out; Clu's retyping is not.
 */

export const VALIDATION_RESULTS_DISPLAY = 'validation_results';

export interface ValidationRow {
  value: string;
  verdict: string;
  ok: boolean;
  lineType: string;
  carrier: string;
  reason: string;
}

function textOf(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : '';
}

const LINE_TYPES: Record<string, string> = {
  mobile: 'Mobile',
  fixed_line: 'Landline',
  fixed_line_or_mobile: 'Landline or mobile',
  voip: 'VoIP',
  toll_free: 'Toll free',
  unknown: 'Unknown',
};

/** A phone line type in words: `fixed_line_or_mobile` is "Landline or mobile". */
export function lineTypeLabel(value: unknown): string {
  const raw = textOf(value).toLowerCase();
  if (!raw) return '';
  if (LINE_TYPES[raw]) return LINE_TYPES[raw];
  const words = raw.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function isPhoneValidation(result: unknown): boolean {
  return asRecord(result)?.kind === 'phone';
}

export function validationRows(result: unknown): ValidationRow[] {
  const rows = asRecord(result)?.results;
  if (!Array.isArray(rows)) return [];
  return rows
    .map(asRecord)
    .filter((row): row is Record<string, unknown> => row !== null)
    .map((row) => ({
      value: textOf(row.value),
      verdict: textOf(row.verdict) || 'unknown',
      ok: Boolean(row.ok),
      lineType: lineTypeLabel(row.line_type),
      carrier: textOf(row.carrier),
      reason: textOf(row.reason),
    }));
}

/**
 * The rows as text, one line a value, as the card shows them:
 * `number|carrier|type|result` for phones, `email|result|reason` for emails.
 * No header.
 */
export function validationCopyText(result: unknown): string {
  const phone = isPhoneValidation(result);
  return validationRows(result)
    .map((row) =>
      phone
        ? [row.value, row.carrier, row.lineType, row.verdict].join('|')
        : [row.value, row.verdict, row.reason].join('|')
    )
    .join('\n');
}

/**
 * Every check in the thread as one text, for "Copy all" on the newest card:
 * empty unless `result` is the newest of two or more checks with rows.
 * The newest is found by the part's own result, never by its id: every
 * approved check carries the same id (`tool-validate_contacts`). A string,
 * so a selector returning it re-renders the card only when it changes.
 */
export function threadValidationCopyText(
  messages: readonly ThreadMessageLike[],
  result: unknown
): string {
  if (!result) return '';
  const checks: Array<{ result: unknown; text: string }> = [];
  for (const message of messages) {
    for (const part of message.content) {
      if (partDisplayType(part) !== VALIDATION_RESULTS_DISPLAY) continue;
      const text = validationCopyText(part.result);
      if (!text) continue;
      checks.push({ result: part.result, text });
    }
  }
  if (checks.length < 2 || checks[checks.length - 1].result !== result) {
    return '';
  }
  return checks.map((check) => check.text).join('\n');
}
